// src/components/pages/ProductDetails.jsx

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getColors } from '../../styles/colors';
import { 
  FaArrowLeft, FaPlus, FaMinus, FaTrash, 
  FaCheck, FaSpinner, FaShoppingCart,
  FaInfoCircle, FaTruck, FaShieldAlt
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  fetchProductByIdAsync,
  clearSelectedProduct
} from '../../redux/slices/productSlice';
import { 
  addToCartAsync, 
  fetchCartItemsAsync, 
  removeFromCartAsync, 
  updateCartAsync
} from '../../redux/slices/cartSlice';
import { setShowLoginModal } from '../../redux/slices/authSlice';
import LoginModal from '../common/LoginModal';

const ProductDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useSelector((state) => state.theme?.mode || 'light');
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  // Get auth state
  const { showLoginModal } = useSelector((state) => state.auth);
  
  // Get products from Redux
  const { 
    selectedProduct,
    isLoading,
    error
  } = useSelector((state) => state.products || {
    selectedProduct: null,
    isLoading: false,
    error: null
  });
  
  // Get cart state from Redux
  const { 
    items: cartItems, 
    isLoading: cartLoading 
  } = useSelector((state) => state.cart || {
    items: [],
    isLoading: false
  });
  
  const [quantity, setQuantity] = useState(1);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // ✅ Loading state for + and - buttons
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  // ========== FETCH PRODUCT DETAILS ==========
  useEffect(() => {
    if (id) {
      dispatch(fetchProductByIdAsync(id));
    }
    
    return () => {
      dispatch(clearSelectedProduct());
    };
  }, [dispatch, id]);

  // ========== FETCH CART ==========
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(fetchCartItemsAsync());
    }
  }, [dispatch]);

  // ========== CHECK AUTHENTICATION ==========
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      dispatch(setShowLoginModal(true));
    }
  }, [dispatch]);

  // ========== GET CART QUANTITY ==========
  const getCartQuantity = (productId) => {
    const productIdStr = String(productId);
    const item = cartItems.find(item => {
      const itemIdStr = String(item.item_id);
      return itemIdStr === productIdStr && item.item_type === 'product';
    });
    return item ? parseInt(item.quantity) : 0;
  };

  // ========== GET CART ID ==========
  const getCartId = (productId) => {
    const productIdStr = String(productId);
    const item = cartItems.find(item => {
      const itemIdStr = String(item.item_id);
      return itemIdStr === productIdStr && item.item_type === 'product';
    });
    return item ? item.cart_id : null;
  };

  // ========== ADD TO CART ==========
  const handleAddToCart = async () => {
    if (!selectedProduct) return;
    
    setIsUpdating(true);
    try {
      await dispatch(addToCartAsync({
        product_id: selectedProduct.id,
        quantity: quantity,
        price: selectedProduct.price
      })).unwrap();
      
      await dispatch(fetchCartItemsAsync());
      
      setSuccessMessage(`✅ Added ${selectedProduct.product_name} to cart!`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (err) {
      console.error('❌ Failed to add to cart:', err);
      alert('Failed to add item to cart. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ========== UPDATE QUANTITY IN CART ==========
  const handleUpdateQuantity = async (cartId, newQuantity) => {
    if (newQuantity < 1) return;
    
    // ✅ Show loading on + and - buttons
    setIsButtonLoading(true);
    setIsUpdating(true);
    
    try {
      const cartItem = cartItems.find(item => item.cart_id === cartId);
      if (!cartItem) {
        alert('Item not found in cart.');
        setIsButtonLoading(false);
        setIsUpdating(false);
        return;
      }
      
      const productId = parseInt(cartItem.item_id);
      await dispatch(updateCartAsync({
        item_id: productId,
        quantity: newQuantity
      })).unwrap();
      
      setSuccessMessage('🔄 Quantity updated!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (err) {
      console.error('❌ Failed to update quantity:', err);
      alert('Failed to update quantity. Please try again.');
    } finally {
      setIsButtonLoading(false);
      setIsUpdating(false);
    }
  };

  // ========== REMOVE FROM CART ==========
  const handleRemoveItem = async (cartId) => {
    if (!window.confirm('Remove this item from cart?')) return;
    
    setIsUpdating(true);
    try {
      await dispatch(removeFromCartAsync(cartId)).unwrap();
      setSuccessMessage('🗑️ Item removed from cart');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('❌ Failed to remove item:', err);
      alert('Failed to remove item. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ========== UPDATE QUANTITY ==========
  const updateQuantity = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  // ========== GET PRODUCT TYPE COLOR ==========
  const getProductTypeColor = (type) => {
    if (type === 'Veg' || type === 'veg') {
      return '#22C55E';
    } else if (type === 'Non-Veg' || type === 'nonveg' || type === 'NonVeg') {
      return '#EF4444';
    }
    return null;
  };

  // ========== RENDER LOADING ==========
  if (isLoading || !selectedProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto"
               style={{ 
                 borderColor: colors.primary,
                 borderTopColor: 'transparent'
               }}></div>
          <p className="mt-4 font-medium" style={{ color: colors.textSecondary }}>
            Loading product details...
          </p>
        </div>
      </div>
    );
  }

  // ========== RENDER ERROR ==========
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" 
           style={{ backgroundColor: colors.background }}>
        <div className="text-center p-8 rounded-2xl max-w-md w-full"
             style={{ 
               backgroundColor: colors.card,
               border: `1px solid ${colors.border}`
             }}>
          <div className="text-4xl mb-4">😕</div>
          <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>
            Product Not Found
          </h3>
          <p className="mb-4" style={{ color: colors.textSecondary }}>
            {typeof error === 'string' ? error : 'Unable to load product details.'}
          </p>
          <button 
            onClick={() => navigate('/cart-service')}
            className="px-6 py-2 rounded-full text-white font-medium transition-all duration-300 hover:scale-105"
            style={{ backgroundColor: colors.primary }}
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const product = selectedProduct;
  const cartQty = getCartQuantity(product.id);
  const cartId = getCartId(product.id);
  const isInCart = cartQty > 0;
  const typeColor = getProductTypeColor(product.type);
  
  // Parse ingredients from in_thali
  const ingredients = product.in_thali ? product.in_thali.split('\n').filter(item => item.trim()) : [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Success Toast */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg flex items-center gap-3"
            style={{ 
              backgroundColor: '#22C55E',
              color: 'white'
            }}
          >
            <FaCheck />
            {successMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => dispatch(setShowLoginModal(false))} 
      />

      {/* Product Details */}
      <div className="max-w-6xl mx-auto px-4 py-6 w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/cart-service')}
          className="flex items-center gap-2 mb-6 px-4 py-2 rounded-full transition-all hover:scale-105"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            color: colors.text
          }}
        >
          <FaArrowLeft />
          <span>Back to Products</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Image */}
          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden shadow-lg"
                 style={{ 
                   backgroundColor: colors.card,
                   border: `1px solid ${colors.border}`
                 }}>
              <img
                src={product.prod_image ? `https://admin.thefoodies.in/public/uploads/products/${product.prod_image}` : '/placeholder-image.jpg'}
                alt={product.product_name || 'Product'}
                className="w-full h-96 object-cover"
                onError={(e) => {
                  e.target.src = '/placeholder-image.jpg';
                }}
              />
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="space-y-6">
            {/* Product Name with Veg/Non-Veg Square Badge with Circle inside */}
            <div className="flex items-center gap-3">
              {/* Square Box with Circle inside - Only color, no icon */}
              {typeColor && (
                <div 
                  className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                  style={{ 
                    backgroundColor: typeColor + '20',
                    border: `2px solid ${typeColor}`
                  }}
                >
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: typeColor }}
                  />
                </div>
              )}
              <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
                {product.product_name || 'Product'}
              </h1>
            </div>

            {/* Price */}
            <div className="flex items-end gap-3">
              <span className="text-3xl font-bold" style={{ color: colors.primary }}>
                ₹{product.price || '0'}
              </span>
              {product.original_price && product.original_price > product.price && (
                <>
                  <span className="text-lg line-through" style={{ color: colors.textSecondary }}>
                    ₹{product.original_price}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: '#EF4444' }}>
                    {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                  </span>
                </>
              )}
            </div>

            {/* Description */}
            {product.in_thali && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: colors.text }}>
                  <FaInfoCircle style={{ color: colors.primary }} />
                  Description
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
                  {ingredients.length > 0 ? ingredients.join(', ') : product.in_thali}
                </p>
              </div>
            )}

            {/* Quantity Selector - Add to Cart */}
            <div className="pt-4 border-t" style={{ borderColor: colors.border }}>
              <div className="flex flex-wrap items-center gap-4">
                {isInCart ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {/* ✅ Minus Button with Loading State */}
                      <button
                        onClick={() => handleUpdateQuantity(cartId, cartQty - 1)}
                        disabled={isButtonLoading || isUpdating || cartLoading}
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ 
                          backgroundColor: isButtonLoading ? '#EF444460' : '#EF4444',
                          opacity: isButtonLoading ? 0.6 : 1
                        }}
                      >
                        {isButtonLoading ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaMinus />
                        )}
                      </button>
                      
                      {/* Quantity Display */}
                      <span className="font-bold text-xl min-w-[32px] text-center" style={{ color: colors.text }}>
                        {isButtonLoading ? '...' : cartQty}
                      </span>
                      
                      {/* ✅ Plus Button with Loading State */}
                      <button
                        onClick={() => handleUpdateQuantity(cartId, cartQty + 1)}
                        disabled={isButtonLoading || isUpdating || cartLoading}
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ 
                          backgroundColor: isButtonLoading ? '#22C55E60' : '#22C55E',
                          opacity: isButtonLoading ? 0.6 : 1
                        }}
                      >
                        {isButtonLoading ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaPlus />
                        )}
                      </button>
                    </div>
                    
                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemoveItem(cartId)}
                      disabled={isUpdating || cartLoading}
                      className="px-4 py-2 rounded-full text-white transition-all hover:scale-105 flex items-center gap-2 disabled:opacity-50"
                      style={{ backgroundColor: '#EF4444' }}
                    >
                      <FaTrash />
                      Remove
                    </button>
                    
                    <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                      Total: ₹{(product.price * cartQty).toFixed(2)}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 w-full">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                        Qty:
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(-1)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-all hover:scale-105"
                          style={{ backgroundColor: '#EF4444' }}
                          disabled={quantity <= 1}
                        >
                          <FaMinus className="text-xs" />
                        </button>
                        <span className="font-bold text-lg min-w-[28px] text-center" style={{ color: colors.text }}>
                          {quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(1)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-all hover:scale-105"
                          style={{ backgroundColor: '#22C55E' }}
                        >
                          <FaPlus className="text-xs" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Add to Cart Button */}
                    <button
                      onClick={handleAddToCart}
                      disabled={isUpdating || cartLoading}
                      className="flex-1 px-6 py-3 rounded-full text-white font-medium transition-all hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50"
                      style={{ 
                        backgroundColor: isUpdating ? colors.primary + '80' : colors.primary,
                        boxShadow: `0 4px 15px ${colors.primary}40`,
                        opacity: isUpdating ? 0.6 : 1
                      }}
                    >
                      {isUpdating ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <FaShoppingCart />
                          Add to Cart - ₹{(product.price * quantity).toFixed(2)}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Features / Highlights */}
            <div className="grid grid-cols-2 gap-3 pt-4">
              <div className="flex items-center gap-3 p-3 rounded-xl"
                   style={{ 
                     backgroundColor: colors.inputBackground || colors.card,
                     border: `1px solid ${colors.border}`
                   }}>
                <FaTruck style={{ color: colors.primary }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: colors.text }}>Free Delivery</p>
                  <p className="text-[10px]" style={{ color: colors.textSecondary }}>On orders above ₹499</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl"
                   style={{ 
                     backgroundColor: colors.inputBackground || colors.card,
                     border: `1px solid ${colors.border}`
                   }}>
                <FaShieldAlt style={{ color: colors.primary }} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: colors.text }}>Fresh Guarantee</p>
                  <p className="text-[10px]" style={{ color: colors.textSecondary }}>100% fresh ingredients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;